import type {
  ApiErrorCode,
  LocalizedText,
  MessageKey,
  PolicyViolation,
} from "@sirdab/returns-shared";

/**
 * Every call returns a value. Expected business outcomes — a blocked customer, a closed
 * store, a policy violation on submit — are DATA the UI branches on, not exceptions.
 * Throwing for them would push handling into an error boundary, and a shopper who hits an
 * error boundary mid-flow goes to WhatsApp instead of finishing the return.
 */
export type ApiResult<T> =
  | ApiSuccess<T>
  | ApiFailureResponse
  | ApiFailureNetwork
  | ApiFailureContract;

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

/** The server answered with the `{ error, reasonText?, violations? }` envelope (docs/06). */
export interface ApiFailureResponse {
  ok: false;
  kind: "response";
  status: number;
  error: ApiErrorCode;
  /**
   * Bilingual, authored server-side. The portal renders the active side and NEVER writes
   * its own explanation of a policy decision (apps/portal/AGENTS.md).
   */
  reasonText: LocalizedText | null;
  violations: PolicyViolation[];
}

/** Offline, DNS failure, aborted request. Nothing is known about the server's intent. */
export interface ApiFailureNetwork {
  ok: false;
  kind: "network";
  cause: unknown;
}

/**
 * The server answered, but the body did not match the shared Zod schema. Surfaced loudly
 * rather than flowing on as `undefined` deep inside a component.
 */
export interface ApiFailureContract {
  ok: false;
  kind: "contract";
  status: number;
  issues: string[];
}

export type ApiFailure =
  | ApiFailureResponse
  | ApiFailureNetwork
  | ApiFailureContract;

export function isOk<T>(result: ApiResult<T>): result is ApiSuccess<T> {
  return result.ok;
}

export function isFailure<T>(result: ApiResult<T>): result is ApiFailure {
  return !result.ok;
}

/**
 * The catalogue key to render for a failure, when the server sent no `reasonText`.
 * A network failure has no server-authored explanation, so callers fall back to the
 * offline banner rather than inventing a cause.
 */
export function failureMessageKey(failure: ApiFailure): MessageKey {
  if (failure.kind !== "response") return "error.internal";
  switch (failure.error) {
    case "order_not_found":
    case "not_found":
      return "error.order_not_found";
    case "customer_blocked":
      return "error.customer_blocked";
    case "returns_disabled":
      return "error.returns_disabled";
    case "policy_violation":
      return "error.policy_violation";
    case "rate_limited":
    case "too_many_requests":
      return "error.rate_limited";
    case "invalid_code":
      return "error.invalid_code";
    case "cancel_not_allowed":
      return "error.cancel_not_allowed";
    default:
      return "error.internal";
  }
}
